/**
 * @file: renderLeadActivity.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * Renders a typed lead-activity payload into a Chinese timeline line. The server
 * stores structured `LeadActivityData` (never a prose string); the client owns
 * all wording.
 */

import {
  isTerminalLeadStatus,
  LeadIntentionLabel,
  LeadStatusLabel,
  LeadVisibilityLabel,
} from '@euler/model/lead/Lead';
import {
  LeadActivityChannelLabel,
  LeadActivityData,
  LeadActivityOutcomeLabel,
  LeadActivityView,
} from '@euler/model/lead/LeadActivity';
import { LeadItemDecisionLabel } from '@euler/model/lead/LeadItem';

export const renderLeadActivityTitle = (activity: LeadActivityView): string => {
  const data = activity.data;
  if (data == null) return '动态';
  return describeLeadActivity(data);
};

/** True for the step that closed a lead: its conversion or a move to a
 * terminal status (lost, cancelled, expired). */
export const isLeadClosingActivity = (
  data: LeadActivityData | null | undefined,
): boolean =>
  data?.type === 'converted' ||
  (data?.type === 'status_changed' && isTerminalLeadStatus(data.to));

// The covered-item names for entries whose title only states a count, so the
// timeline can list which items were involved (e.g. 新增 3 个保养项 → names).
export const renderLeadActivityItemNames = (
  activity: LeadActivityView,
): string | null => {
  const data = activity.data;
  if (data?.type !== 'items_added') return null;
  const names = data.items.map(i => i.name).filter(Boolean);
  return names.length ? names.join('、') : null;
};

export const describeLeadActivity = (data: LeadActivityData): string => {
  switch (data.type) {
    case 'created':
      return `创建商机（${data.items.length} 个保养项）`;
    case 'items_added':
      return `新增 ${data.items.length} 个保养项`;
    case 'item_removed':
      return `移除保养项「${data.item.name}」`;
    case 'item_decision_set':
      return data.decision
        ? `「${data.item.name}」标记为${LeadItemDecisionLabel[data.decision]}`
        : `清除「${data.item.name}」的维修意向`;
    case 'reminder_sent':
      return data.audience === 'staff'
        ? `第 ${data.attempt} 次员工跟进提醒`
        : `第 ${data.attempt} 次客户提醒`;
    case 'target_date_changed':
      return `目标服务日期调整为 ${data.to}`;
    case 'assignee_changed':
      return `负责人变更：${data.fromName ?? '—'} → ${data.toName}`;
    case 'visibility_changed':
      return `可见范围变更：${LeadVisibilityLabel[data.from]} → ${
        LeadVisibilityLabel[data.to]
      }`;
    case 'intention_changed':
      return `到店意向变更：${LeadIntentionLabel[data.from]} → ${
        LeadIntentionLabel[data.to]
      }`;
    case 'status_changed':
      return `状态变更：${LeadStatusLabel[data.from]} → ${
        LeadStatusLabel[data.to]
      }${data.reason ? `（${data.reason}）` : ''}`;
    case 'converted':
      if (data.evidence?.kind === 'arrived')
        return data.evidence.orderNo
          ? `已到店转化，关联工单 ${data.evidence.orderNo}`
          : '已到店转化';
      if (data.evidence?.kind === 'coupon_redeemed')
        return '优惠券已核销，商机已转化';
      return data.orderNo ? `已转化为订单 ${data.orderNo}` : '商机已转化';
    case 'reservation_created':
      return '已创建预约';
    case 'reservation_cancelled':
      return '预约已取消';
    case 'outreach': {
      const parts = [`${LeadActivityChannelLabel[data.channel]}跟进`];
      if (data.outcome) parts.push(LeadActivityOutcomeLabel[data.outcome]);
      return parts.join(' · ');
    }
  }
};
